import { faClose } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, Stack } from '@mui/material';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Tooltip, {
  type TooltipProps,
  tooltipClasses
} from '@mui/material/Tooltip';
import dayjs from 'dayjs';
import { useAtom, useAtomValue } from 'jotai';
import { type MouseEvent, useEffect, useMemo, useState } from 'react';

import LoadingIndicator from '@/components/LoadingIndicator';
import { YEAR_MONTH_DAY_TIME_FORMAT } from '@/constants/date.constants';
import { useFetchDeviceDetailsByDeviceUID } from '@/features/DeviceCreate/DeviceCreate.hooks';
import type { LogTypeStatusDataModel } from '@/features/DevicesTable/DevicesTable.types';
import SOURCE_TYPE from '@/features/SourceCreate/SourceCreate.constants';
import { useSourceDialog } from '@/features/SourceCreate/SourceCreate.hooks';
import { useFetchSourceDetailsBySourceUID } from '@/features/SourceCreate/SourceCreate.service';
import type { LimaDetailedSourceModel } from '@/features/SourceCreate/SourceCreate.types';
import { isValidUUID } from '@/features/SourceCreate/SourceCreate.utils';
import {
  CustomEventTypes,
  customEventAtom
} from '@/pages/Administration/Devices';
import DeviceDetailContextMenu from '@/pages/Administration/Devices/DeviceContextMenu/DeviceDetailContextMenu';
import SourceDetailContextMenu from '@/pages/Administration/Devices/DeviceContextMenu/SourceDetailContextMenu';
import {
  CLOUD_SOURCE_DETAILS_PAGE,
  DEVICE_DETAILS_PAGE,
  useDeviceSelectionParams
} from '@/pages/Administration/Devices/Devices.hooks';

import {
  filteredLimaSourcesAtom,
  selectedDeviceDetailsAtom,
  selectedMicrosoftSourceDetailsAtom,
  selectedSourceDetailsAtom
} from '../Devices.store';
import DeviceCard from './DeviceCard/DeviceCard';
import useStyles from './DeviceDetails.styles';
import type { DeviceDetailProps } from './DeviceDetails.types';
import SourceCard from './SourceCard/SourceCard';
import MicrosoftProviders from './ViewDataProviders/MicrosoftProviders';

const DETAILS_CARD_STYLE = { width: '100%' } as const;
const HEADER_TITLE_STYLE = { width: '100%', margin: '0' } as const;
const CLOSE_ICON_STYLE = { margin: '1px' } as const;
const EMPTY_STATE_HEADER_STYLE = {
  width: '100%',
  margin: '0',
  padding: '.5rem'
} as const;
const EMPTY_STATE_BODY_STYLE = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%'
} as const;

const OverflowTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.vars.palette.background.elevation2,
    color: 'var(--mui-palette-text-primary)',
    boxShadow: theme.shadows[1],
    fontSize: 11
  }
}));

/**
 * Displays selected device/source details and keeps the detail panel state in sync
 * with auto-refresh and selection updates.
 *
 * @param props Component properties.
 * @returns Device details side panel.
 */
const DeviceDetails = ({
  closeDeviceDetail,
  isMainDevicePage
}: DeviceDetailProps) => {
  const { classes } = useStyles();
  const {
    deviceUid: selectedDeviceUid,
    page: selectedPage,
    sourceUid: selectedSourceUid
  } = useDeviceSelectionParams();
  const [title, setTitle] = useState('');
  const [tooltipEnabled, setTooltipEnabled] = useState(false);
  const [selectedDeviceDetail] = useAtom(selectedDeviceDetailsAtom);
  const [selectedSourceDetail, setSelectedSourceDetail] = useAtom(
    selectedSourceDetailsAtom
  );

  const [selectedMicrosoftSourceDetail, setSelectedMicrosoftSourceDetail] =
    useAtom(selectedMicrosoftSourceDetailsAtom);

  const [, setCustomEvent] = useAtom(customEventAtom);
  const customEventValue = useAtomValue(customEventAtom);
  const deviceQuery = useFetchDeviceDetailsByDeviceUID(
    selectedDeviceUid,
    selectedPage
  );
  const [filteredLimaSources] = useAtom(filteredLimaSourcesAtom);

  const srcTypeId = filteredLimaSources.find(
    (src) => src.source_uid === selectedSourceUid
  )?.source_type_id;
  const sourceQuery = useFetchSourceDetailsBySourceUID(
    selectedSourceUid,
    srcTypeId ?? 0
  );

  const { deleteLogTypeStatusMutation } = useSourceDialog();
  const isDeviceSelected =
    isValidUUID(selectedDeviceUid) && !isValidUUID(selectedSourceUid);

  const isSourceSelected = isValidUUID(selectedSourceUid);
  const isDeviceDetailsView =
    isDeviceSelected && selectedPage === DEVICE_DETAILS_PAGE;
  const isSourceDetailsView =
    isSourceSelected && selectedPage === CLOUD_SOURCE_DETAILS_PAGE;
  const showDetailsView = isDeviceDetailsView || isSourceDetailsView;

  /**
   * Enables title tooltip only when content is visually truncated.
   *
   * @param event Mouse enter event from title span.
   */
  const showOverflowTooltip = ({ currentTarget }: MouseEvent<Element>) => {
    if (currentTarget.scrollWidth > currentTarget.clientWidth) {
      setTooltipEnabled(true);
    }
  };

  const hideOverflowTooltip = () => setTooltipEnabled(false);

  /**
   * Closes the detail panel and clears selected source context.
   */
  const closeDetailPage = () => {
    closeDeviceDetail();
  };

  /**
   * Refetches detail data for the currently selected panel context.
   *
   * @remarks
   * Query triggers are intentionally gated by collapsed state and valid UUID checks.
   */
  const triggerDeviceDetailApi = () => {
    if (isDeviceDetailsView && isValidUUID(selectedDeviceUid)) {
      if (!deviceQuery.isFetching) {
        deviceQuery.refetch();
      }
    } else if (isSourceDetailsView && isValidUUID(selectedSourceUid)) {
      if (!sourceQuery.isFetching) {
        sourceQuery.refetch();
      }
    }
  };

  /**
   * Resolves selected category names for source-specific category-aware status logic.
   *
   * @param sourceDet Source details model.
   * @returns Selected category names.
   */
  const getSelectedCategories = (
    sourceDet: LimaDetailedSourceModel
  ): string[] => {
    const isAwsCategory =
      sourceDet?.source_type_id === SOURCE_TYPE.AWS_CLOUDTRAIL ||
      sourceDet?.source_type_id === SOURCE_TYPE.AWS_SECURITY_HUB;

    if (isAwsCategory) {
      return sourceDet?.connection_inputs?.region || [];
    }

    // If data_providers exists, return its keys as categories
    if (sourceDet?.source_type_id === SOURCE_TYPE.GOOGLE_STORAGE) {
      return Object.keys(sourceDet?.connection_inputs?.data_providers || {});
    }

    if (sourceDet?.source_type_id === SOURCE_TYPE.AWS_S3) {
      return (
        sourceDet?.connection_inputs?.s3_data_providers?.map(
          (provider: { name: string }) => provider.name
        ) || []
      );
    }

    return sourceDet?.connection_inputs?.category || [];
  };

  useEffect(() => {
    const deviceDetailData = deviceQuery.data;
    if (
      !deviceQuery.isRefetching &&
      deviceQuery.isFetched &&
      deviceDetailData
    ) {
      if (deviceDetailData && selectedPage === DEVICE_DETAILS_PAGE) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setTitle(deviceDetailData?.host_name || '');
      }
    }
  }, [
    deviceQuery.data,
    deviceQuery.isFetched,
    deviceQuery.isRefetching,
    selectedPage
  ]);

  /**
   * Enhances source details with derived status collections used by the details view.
   *
   * @param sourceDetail Raw source detail response.
   * @returns Enhanced source detail model.
   */
  const enhanceLimaSourceDetailsResponse = (
    sourceDetail: LimaDetailedSourceModel
  ) => {
    const sourceDet = { ...sourceDetail };
    // Get selected categories based on source selected
    const selectedCategories = getSelectedCategories(sourceDet);

    // Filtering Failed + selected categories
    sourceDet.failed_categories = (
      (sourceDet?.log_type_statuses as LogTypeStatusDataModel[]) || []
    )
      .filter((logTypeStatus) => {
        const isFailed = logTypeStatus.status?.toLowerCase() === 'failed';

        // keep only if in selected categories
        const isInSelected = selectedCategories.includes(
          logTypeStatus.log_type
        );

        return isFailed && isInSelected;
      })
      .map((logTypeStatus) => ({
        ...logTypeStatus,
        event_time: dayjs
          .utc(logTypeStatus?.last_modified_time)
          .local()
          .format(YEAR_MONTH_DAY_TIME_FORMAT)
      }))
      .sort(
        (a, b) => dayjs(b.event_time).valueOf() - dayjs(a.event_time).valueOf()
      );

    // --------------------------------------------
    // This condition is added as part of MOL-897 to ensure that the category status correctly reflects categories removed by the UI.
    // --------------------------------------------
    const allStatuses = (sourceDet?.log_type_statuses ||
      []) as LogTypeStatusDataModel[];
    const unselectedFailedStatuses = allStatuses.filter(
      (item) =>
        !selectedCategories.includes(item.log_type) &&
        item.status?.toLowerCase() === 'failed'
    );

    if (unselectedFailedStatuses.length > 0) {
      const logTypesToDelete = unselectedFailedStatuses.map((i) => i.log_type);
      deleteLogTypeStatusMutation.mutate({
        sourceUid: sourceDet?.source_uid,
        logTypes: logTypesToDelete
      });
    }

    // Salesforce failed categories are handled separately
    sourceDet.custom_object_status = (
      sourceDet?.log_type_statuses as LogTypeStatusDataModel[]
    )?.filter(
      (logTypeStatus) => !logTypeStatus.log_type.startsWith('Salesforce_')
    );

    sourceDet.events_availability_history = (
      sourceDet?.log_type_statuses as LogTypeStatusDataModel[]
    )
      ?.map((logTypeStatus) => {
        if (logTypeStatus?.log_type?.startsWith('Salesforce_')) {
          return {
            ...logTypeStatus,
            event_time: dayjs
              .utc(logTypeStatus?.log_type?.split('Salesforce_')[1])
              .local()
              .format(YEAR_MONTH_DAY_TIME_FORMAT)
          };
        }
        return null; // Ensure non-matching items are filtered out
      })
      .filter(Boolean) // Remove null/undefined values from the array
      .sort((a, b) => {
        if (!a?.event_time || !b?.event_time) return 0; // Prevent errors
        return dayjs(b.event_time).valueOf() - dayjs(a.event_time).valueOf();
      }) as LogTypeStatusDataModel[];
    return sourceDet;
  };

  useEffect(() => {
    let sourceDet = sourceQuery.data as LimaDetailedSourceModel;

    if (!sourceQuery.isRefetching && sourceQuery.isFetched && sourceDet) {
      // Failed categories should be displayed only when they are part of the currently selected categories.

      if (srcTypeId !== SOURCE_TYPE.MICROSOFT) {
        sourceDet = enhanceLimaSourceDetailsResponse(sourceDet);
        setSelectedSourceDetail(sourceDet);
        if (sourceDet && selectedPage === CLOUD_SOURCE_DETAILS_PAGE) {
          // eslint-disable-next-line react-hooks/set-state-in-effect
          setTitle(
            sourceDet?.source_type?.name?.trim().replace(/\s+/g, '') ?? ''
          );
        }
      } else if (srcTypeId === SOURCE_TYPE.MICROSOFT) {
        if (Array.isArray(sourceDet) && sourceDet.length > 0) {
          const enhancedSourceDetails: LimaDetailedSourceModel[] = [];
          sourceDet.forEach((src) => {
            enhanceLimaSourceDetailsResponse(src);
            enhancedSourceDetails.push(src);
          });
          setSelectedMicrosoftSourceDetail(enhancedSourceDetails);
        }
        if (sourceDet && selectedPage === CLOUD_SOURCE_DETAILS_PAGE) {
          setTitle('Microsoft');
        }
      }
    }
  }, [
    selectedPage,
    setSelectedMicrosoftSourceDetail,
    setSelectedSourceDetail,
    sourceQuery.data,
    sourceQuery.isFetched,
    sourceQuery.isRefetching,
    srcTypeId
  ]);

  useEffect(() => {
    // trigger device detail on auto refresh
    if (
      customEventValue === CustomEventTypes.REFRESH_DEVICE_DETAIL ||
      customEventValue === CustomEventTypes.REFRESH_CLOUD_SOURCE_DETAIL
    ) {
      setCustomEvent(CustomEventTypes.NONE);
      triggerDeviceDetailApi();
    }
  }, [customEventValue]);

  const deviceDetailPaneTitle = useMemo(() => {
    if (
      selectedPage === DEVICE_DETAILS_PAGE &&
      selectedDeviceDetail?.device_type?.type
    ) {
      return `${selectedDeviceDetail.device_type.type}: ${title}`;
    }
    return title;
  }, [selectedDeviceDetail?.device_type?.type, selectedPage, title]);

  const renderCloseButton = () => (
    <Button
      className={classes.deviceDetailCardHeaderButton}
      variant="outlined"
      onClick={closeDetailPage}
      size="medium"
    >
      <FontAwesomeIcon style={CLOSE_ICON_STYLE} icon={faClose} size="lg" />
    </Button>
  );

  const renderContextMenu = () => {
    if (
      isMainDevicePage &&
      selectedDeviceDetail?.device_type_id !== 1 &&
      selectedPage === DEVICE_DETAILS_PAGE
    ) {
      return <DeviceDetailContextMenu />;
    }

    if (selectedPage === CLOUD_SOURCE_DETAILS_PAGE) {
      return <SourceDetailContextMenu />;
    }

    return '';
  };

  const renderPanelHeader = () => (
    <div className={classes.deviceDetailCardHeader}>
      <h4 style={HEADER_TITLE_STYLE}>
        <OverflowTooltip
          placement="top-start"
          followCursor
          title={title}
          open={tooltipEnabled}
          onClose={hideOverflowTooltip}
        >
          <span
            className={classes.deviceDetailCardHeaderSpan}
            onMouseEnter={showOverflowTooltip}
          >
            {deviceDetailPaneTitle}
          </span>
        </OverflowTooltip>
        <div className={classes.deviceDetailCardHeaderButtonWrapper}>
          {renderCloseButton()}
          {renderContextMenu()}
        </div>
      </h4>
    </div>
  );

  const renderEmptyState = () => (
    <Stack sx={{ height: '100%' }}>
      <h4 style={EMPTY_STATE_HEADER_STYLE}>
        <div className={classes.deviceDetailCardHeaderButtonWrapper}>
          {renderCloseButton()}
        </div>
      </h4>
      <div style={EMPTY_STATE_BODY_STYLE}>
        {isSourceSelected
          ? 'Select a cloud source to view details'
          : 'Select a device to view details'}
      </div>
    </Stack>
  );

  return (
    <Card sx={DETAILS_CARD_STYLE} className={classes.deviceDetailsCard}>
      <LoadingIndicator
        noBackground
        isLoading={deviceQuery.isFetching || sourceQuery.isFetching}
      />

      {showDetailsView ? (
        <>
          {renderPanelHeader()}

          {isDeviceDetailsView && selectedDeviceDetail && (
            <DeviceCard
              items={selectedDeviceDetail}
              isMainDevicePage={isMainDevicePage}
            />
          )}

          {isSourceDetailsView &&
            selectedSourceDetail &&
            srcTypeId !== SOURCE_TYPE.MICROSOFT && (
              <SourceCard
                key={selectedSourceDetail?.source_uid}
                item={selectedSourceDetail}
              />
            )}

          {isSourceDetailsView &&
            selectedSourceDetail &&
            srcTypeId === SOURCE_TYPE.MICROSOFT && (
              <MicrosoftProviders
                key={selectedMicrosoftSourceDetail?.[0]?.source_uid}
                items={selectedMicrosoftSourceDetail}
              />
            )}
        </>
      ) : (
        renderEmptyState()
      )}
    </Card>
  );
};

export default DeviceDetails;
